import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Message {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
    intent?: string;
    confidence?: number;
    imageUrl?: string;
    voiceUrl?: string;
}

const WhatsAppTest: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [voiceUrl, setVoiceUrl] = useState('');
    const [tenantID, setTenantID] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Load tenant ID from localStorage
    useEffect(() => {
        const storedTenantID = localStorage.getItem('tenantID');
        setTenantID(storedTenantID || '');
    }, []);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async () => {
        if (!inputMessage.trim() && !imageUrl.trim() && !voiceUrl.trim()) {
            return;
        }

        if (!tenantID.trim()) {
            setError('Please enter a Tenant ID');
            return;
        }

        // Save current values before clearing
        const currentImageUrl = imageUrl;
        const currentVoiceUrl = voiceUrl;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: inputMessage || (currentImageUrl ? 'Image' : currentVoiceUrl ? 'Voice message' : ''),
            isUser: true,
            timestamp: new Date(),
            ...(currentImageUrl && { imageUrl: currentImageUrl }),
            ...(currentVoiceUrl && { voiceUrl: currentVoiceUrl }),
        };

        setMessages((prev) => [...prev, userMessage]);
        setLoading(true);
        setError(null);
        setInputMessage('');
        setImageUrl('');
        setVoiceUrl('');

        try {
            const response = await fetch(`${API_URL}/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    tenantID: tenantID.trim(),
                    message: inputMessage || '',
                    ...(currentImageUrl && { imageUrl: currentImageUrl }),
                    ...(currentVoiceUrl && { voiceUrl: currentVoiceUrl }),
                }),
            });

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                // If response is not valid JSON, treat as request failure
                throw new Error('request_failed');
            }

            if (!response.ok) {
                // Show user-friendly message for API errors
                throw new Error('request_failed');
            }

            if (data.success && data.data) {
                const botMessage: Message = {
                    id: data.data.messageId || Date.now().toString(),
                    text: data.data.response || 'No response',
                    isUser: false,
                    timestamp: new Date(),
                    intent: data.data.intent,
                    confidence: data.data.confidence,
                };

                setMessages((prev) => [...prev, botMessage]);
            } else {
                throw new Error('request_failed');
            }
        } catch (err) {
            // Show user-friendly error message instead of technical error details
            // Handle different error types gracefully
            let userFriendlyMessage = 'Sorry, something went wrong. Please try again.';

            // Check if it's a network error (like "Failed to fetch")
            const errorMessage = err instanceof Error ? err.message : String(err);
            if (errorMessage.includes('Failed to fetch') || errorMessage === 'request_failed') {
                userFriendlyMessage = 'Sorry, something went wrong. Please try again.';
            } else if (errorMessage.includes('NetworkError') || errorMessage.includes('network')) {
                userFriendlyMessage = 'Network error. Please check your connection and try again.';
            }

            setError(userFriendlyMessage);
            const errorMsg: Message = {
                id: Date.now().toString(),
                text: userFriendlyMessage,
                isUser: false,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="w-full min-h-screen bg-main-dark flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark px-8 py-6 shadow-xl border-b border-white/5">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white tracking-tight">WhatsApp Test Interface</h1>
                                <p className="text-gray-400 text-sm">Test your WhatsApp Business API integration</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 px-4 py-2 bg-main-dark/50 rounded-xl border border-white/5">
                                <span className="text-gray-400 text-sm">Tenant ID:</span>
                                <input
                                    type="text"
                                    value={tenantID}
                                    onChange={(e) => {
                                        setTenantID(e.target.value);
                                        localStorage.setItem('tenantID', e.target.value);
                                    }}
                                    placeholder="Enter Tenant ID"
                                    className="bg-transparent text-white text-sm w-64 focus:outline-none placeholder-gray-500"
                                />
                            </div>
                            <span className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${tenantID ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}`}>
                                {tenantID ? 'Ready' : 'No Tenant'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 bg-main-dark pb-48">
                <div className="max-w-4xl mx-auto">
                    {messages.length === 0 && (
                        <div className="text-center py-20">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full mb-6 border border-green-500/20">
                                <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-3">Start a Conversation</h2>
                            <p className="text-gray-400 max-w-md mx-auto">Send a message below to test your WhatsApp Business API integration. You can also attach images or voice messages.</p>
                        </div>
                    )}
                    <div className="space-y-4">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`flex items-end gap-2 max-w-xs lg:max-w-md ${message.isUser ? 'flex-row-reverse' : ''}`}>
                                    {/* Avatar */}
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-md ${message.isUser
                                        ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                                        : 'bg-gradient-to-br from-green-500 to-green-600'}`}>
                                        {message.isUser ? (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        )}
                                    </div>

                                    {/* Message bubble */}
                                    <div
                                        className={`px-4 py-3 rounded-2xl shadow-lg border ${message.isUser
                                            ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-br-sm border-purple-500/30'
                                            : 'bg-main-dark/80 backdrop-blur-sm text-gray-200 rounded-bl-sm border-white/5'
                                            }`}
                                    >
                                        {/* Display image if present */}
                                        {message.imageUrl && (
                                            <div className="mb-2 rounded-lg overflow-hidden">
                                                <img
                                                    src={message.imageUrl}
                                                    alt="Shared image"
                                                    className="max-w-full h-auto rounded-lg"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        const errorDiv = document.createElement('div');
                                                        errorDiv.className = 'text-xs p-2 bg-red-500/20 rounded-lg';
                                                        errorDiv.textContent = 'Failed to load image';
                                                        target.parentElement?.appendChild(errorDiv);
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Display voice if present */}
                                        {message.voiceUrl && (
                                            <div className="mb-2">
                                                <audio
                                                    controls
                                                    src={message.voiceUrl}
                                                    className="w-full"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLAudioElement;
                                                        const errorDiv = document.createElement('div');
                                                        errorDiv.className = 'text-xs p-2 bg-red-500/20 rounded-lg';
                                                        errorDiv.textContent = 'Failed to load audio';
                                                        target.parentElement?.appendChild(errorDiv);
                                                    }}
                                                >
                                                    Your browser does not support the audio element.
                                                </audio>
                                            </div>
                                        )}

                                        {/* Display text message if present */}
                                        {message.text && (
                                            <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                                        )}

                                        <div className={`flex items-center justify-between mt-2 text-xs ${message.isUser ? 'text-purple-200' : 'text-gray-500'}`}>
                                            <span>{formatTime(message.timestamp)}</span>
                                            {!message.isUser && message.intent && (
                                                <span className="ml-2 px-2 py-0.5 bg-green-500/20 rounded-full text-green-400 text-xs font-medium border border-green-500/30">
                                                    {message.intent} ({((message.confidence || 0) * 100).toFixed(0)}%)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="flex items-end gap-2">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-md bg-gradient-to-br from-green-500 to-green-600">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="bg-main-dark/80 backdrop-blur-sm text-gray-200 px-4 py-3 rounded-2xl rounded-bl-sm shadow-lg border border-white/5">
                                        <div className="flex space-x-1.5">
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Error Message */}
            {
                error && (
                    <div className="px-8 py-2">
                        <div className="max-w-4xl mx-auto bg-red-500/10 backdrop-blur-sm text-red-200 px-4 py-2 rounded-xl shadow-lg border-l-4 border-red-500">
                            {error}
                        </div>
                    </div>
                )
            }

            {/* Input Area */}
            <div className="bg-gradient-to-t from-main-medium via-main-medium to-transparent px-8 py-6 fixed bottom-0 left-0 right-0 backdrop-blur-md border-t border-white/5">
                <div className="max-w-4xl mx-auto">
                    {/* Optional inputs - collapsible style */}
                    <div className="mb-4 grid grid-cols-2 gap-3">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                value={imageUrl}
                                onChange={(e) => setImageUrl(e.target.value)}
                                placeholder="Image URL (optional)"
                                className="w-full pl-10 pr-4 py-2.5 bg-main-dark/50 backdrop-blur-sm text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-main-dark/70 placeholder-gray-500 text-sm shadow-md transition-all duration-200 border border-white/5"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                value={voiceUrl}
                                onChange={(e) => setVoiceUrl(e.target.value)}
                                placeholder="Voice URL (optional)"
                                className="w-full pl-10 pr-4 py-2.5 bg-main-dark/50 backdrop-blur-sm text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-main-dark/70 placeholder-gray-500 text-sm shadow-md transition-all duration-200 border border-white/5"
                            />
                        </div>
                    </div>

                    {/* Main input */}
                    <div className="flex space-x-3">
                        <div className="flex-1 relative">
                            <textarea
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type a message..."
                                rows={1}
                                className="w-full px-4 py-3 bg-main-dark/50 backdrop-blur-sm text-white rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-main-dark/70 placeholder-gray-500 shadow-md transition-all duration-200 border border-white/5"
                            />
                        </div>
                        <button
                            onClick={sendMessage}
                            disabled={loading || (!inputMessage.trim() && !imageUrl.trim() && !voiceUrl.trim())}
                            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Send
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppTest;
